import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowUp, FaChevronUp, FaBackspace } from "react-icons/fa";
import { lang_icon_black } from "../../assets";

const AlphaNumericKeyboardModal = ({
  onClose,
  onKeyPress,
  mode = "alphanumeric",
  width = "90%",
  maxWidth = "26%",
  height = "90%",
  maxHeight = "50%",
  right = "3rem",
  top = "50%",
}) => {
  const { i18n } = useTranslation();
  const [isUpperCase, setIsUpperCase] = useState(false);
  const [showSymbols, setShowSymbols] = useState(false);
  const [showNumericOnly, setShowNumericOnly] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [keyboardLang, setKeyboardLang] = useState(i18n.language);

  const heightPercent = parseInt(maxHeight?.replace("%", "")) || 60;
  // const fontSize = Math.min(32, Math.max(18, Math.floor(heightPercent * 0.4)));
  const fontSize = Math.min(42, Math.max(24, Math.floor(heightPercent * 0.5)));

  const iconSize = fontSize + 2;

  const handleKeyPress = (key) => {
    switch (key) {
      case "TOGGLE_CASE":
        setIsUpperCase((prev) => !prev);
        break;
      case "SHOW_SYMBOLS":
        setShowSymbols(true);
        setShowNumericOnly(false);
        break;
      case "BACK_ALPHA":
        setShowSymbols(false);
        setShowNumericOnly(false);
        break;
      case "?123":
        setShowSymbols(true);
        break;
      case "LANG_TOGGLE":
        setKeyboardLang((prev) => (prev === "ar" ? "en" : "ar"));
        break;
      default:
        setSelectedKey(key);
        const processedKey =
          isUpperCase &&
          keyboardLang === "en" &&
          key.length === 1 &&
          /[a-z]/i.test(key)
            ? key.toUpperCase()
            : key;
        onKeyPress(processedKey);
    }
  };

  const renderKey = (key) => {
    const dynamicStyle = {
      minHeight: "48px",
      height: "100%",
      fontSize: `${fontSize}px`,
      flex: key === "SPACE" ? 2 : 1,
    };

    let content = key;
    if (key === "DEL") content = <FaBackspace size={iconSize} />;
    else if (key === "TOGGLE_CASE") content = <FaArrowUp size={iconSize} />;
    else if (key === "LANG_TOGGLE")
      content = (
        <img
          src={lang_icon_black}
          alt="Lang"
          className="w-6 h-6 object-contain"
        />
      );
    else if (key === "↵")
      content = <FaChevronUp className="text-green-700" size={iconSize} />;
    else if (key === "SPACE") content = keyboardLang === "ar" ? "مسافة" : "␣";
    else if (key === "BACK_ALPHA") {
      content = keyboardLang === "ar" ? "ا ب ت" : "ABC";
    } else if (
      isUpperCase &&
      keyboardLang === "en" &&
      key.length === 1 &&
      /[a-z]/i.test(key)
    ) {
      content = key.toUpperCase();
    }

    return (
      <button
        key={key}
        onClick={() => handleKeyPress(key)}
        className={`rounded-lg shadow  outline-none focus:outline-none  bg-gray-100 flex items-center justify-center ${
          selectedKey === key ? "bg-red-100 border-2 border-red-400" : ""
        }`}
        //       className={`rounded-lg shadow  flex items-center justify-center transition-all duration-150 ease-in-out
        // ${selectedKey === key ? "bg-red-300 border-2 border-red-500" : "bg-[#f3f4f6]"}
        // hover:bg-[#e5e7eb]`}

        style={dynamicStyle}
      >
        {content}
      </button>
    );
  };

  const isArabic = keyboardLang === "ar";
  const direction = "ltr"; // ← always LTR as per your latest instruction

  // Regular layouts
  const arabicRows = [
    ["ض", "ص", "ث", "ق", "ف", "غ", "ع", "ه", "خ", "ح", "ج"],
    ["ش", "س", "ي", "ب", "ل", "ا", "ت", "ن", "م", "ك", "ط"],
    ["ئ", "ء", "ؤ", "ر", "لا", "ى", "ة", "و", "DEL"],
    ["ز", "د", "?123", "@", "SPACE", ".", "LANG_TOGGLE", "↵"],
  ];

  const englishRows = [
    ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
    ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
    ["TOGGLE_CASE", "@", "z", "x", "c", "v", "b", "n", "m", ".", "DEL"],
    ["?123", "SPACE", "LANG_TOGGLE", "↵"],
  ];

  const symbolRows = [
    ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
    ["@", "#", "$", "_", "&", "-", "+", "(", ")"],
    ["*", '"', "'", ":", ";", "!", "?", "/", "=", "DEL"],
    ["BACK_ALPHA", "SPACE", "LANG_TOGGLE", "↵"],
  ];

  // let layoutRows = showSymbols
  //   ? symbolRows
  //   : isArabic
  //   ? arabicRows
  //   : englishRows;

  const numericRows = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["-", "0", "."],
    ["DEL", "↵"],
  ];

  // Final layout selection
  let layoutRows = [];

  if (mode === "numeric") {
    layoutRows = numericRows;
  } else if (showSymbols) {
    layoutRows = symbolRows;
  } else {
    layoutRows = isArabic ? arabicRows : englishRows;
  }
  return (
    <div className="fixed inset-0 z-50 bg-transparent pointer-events-none w-full h-full">
      <div
        className="absolute z-50 pointer-events-auto bg-white p-4 rounded-xl shadow-lg overflow-hidden flex flex-col"
        style={{
          width,
          maxWidth,
          height,
          maxHeight,
          right,
          top,
          transform: "translateY(-50%)",
        }}
      >
        <div
          className="flex flex-col gap-2 flex-grow justify-end"
          dir={direction}
        >
          {layoutRows.map((row, index) => (
            <div key={index} className="flex gap-2 flex-1 justify-between">
              {row.map(renderKey)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AlphaNumericKeyboardModal;
